import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const ContactForm: React.FC = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  
  // Get reCAPTCHA token
  const { executeRecaptcha } = useGoogleReCaptcha();
  
  // Handle contact form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current || !executeRecaptcha) return;
    
    // Show sending status
    setFormStatus('sending');
    
    try {
      // Get reCAPTCHA token
      const token = await executeRecaptcha('contact_form');
      
      // EmailJS credentials
      const serviceId = 'service_4vac5g7';
      const templateId = 'template_8n1vz0d';
      const publicKey = 'ZbL1oqN2e8HZITjQ_'; // EmailJS public key
      
      // Check if the reCAPTCHA score is good enough (token being valid means human user)
      if (!token) {
        throw new Error('reCAPTCHA validation failed');
      }

      // Prepare template parameters - these must match the template variables in EmailJS
      const templateParams = {
        name: contactName,      // Parameter: {{name}}
        email: contactEmail,    // Parameter: {{email}}
        subject: contactSubject, // Parameter: {{subject}}
        message: contactMessage  // Parameter: {{message}}
      };
      
      // Send email using EmailJS
      await emailjs.send(serviceId, templateId, templateParams, { publicKey: publicKey });
      
      // Email sent successfully
      setFormStatus('success');
      
      // Reset form
      setContactName('');
      setContactEmail('');
      setContactSubject('');
      setContactMessage('');
      
      // Reset status after 5 seconds
      setTimeout(() => setFormStatus('idle'), 5000);
    } catch (error) {
      // Handle error
      console.error('Email sending failed:', error);
      setFormStatus('error');
      
      // Reset status after 5 seconds
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };
  
  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">Name</label>
          <input
            type="text"
            id="name"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            className="w-full px-4 py-3 border border-primary-200 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
            placeholder="Your name"
            required
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">Email</label>
          <input
            type="email"
            id="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            className="w-full px-4 py-3 border border-primary-200 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
            placeholder="you@yourcompany.com"
            required
          />
        </div>
      </div>
      <div>
        <label htmlFor="subject" className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">Subject</label>
        <input
          type="text"
          id="subject"
          value={contactSubject}
          onChange={(e) => setContactSubject(e.target.value)}
          className="w-full px-4 py-3 border border-primary-200 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
          placeholder="What's this about?"
          required
        />
      </div>
      <div>
        <label htmlFor="message" className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">Message</label>
        <textarea
          id="message"
          rows={5}
          value={contactMessage}
          onChange={(e) => setContactMessage(e.target.value)}
          className="w-full px-4 py-3 border border-primary-200 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600 resize-none"
          placeholder="What are you building?"
          required
        ></textarea>
      </div>
      
      {/* Status messages */}
      {formStatus === 'success' && (
        <div className="p-4 bg-green-50 text-green-700 rounded-xl border border-green-200 font-medium">
          Sent. I'll reply soon.
        </div>
      )}
      
      {formStatus === 'error' && (
        <div className="p-4 bg-red-50 text-red-700 rounded-xl border border-red-200 font-medium">
          That didn't send. Email me directly: ben@bensmithdev.com
        </div>
      )}
      
      <button
        type="submit"
        disabled={formStatus === 'sending'}
        className={`w-full font-semibold py-3 px-8 rounded-full transition-all duration-300 ${
          formStatus === 'sending' 
            ? 'bg-primary-400 cursor-not-allowed opacity-70' 
            : 'bg-primary-600 hover:bg-primary-700 hover:shadow-lg hover:scale-[1.02] text-white'
        }`}
      >
        {formStatus === 'sending' ? 'Sending...' : 'Send'}
      </button>
    </form>
  );
};

export default ContactForm;